import moment from 'moment';
import isArray from 'lodash/isArray';
import reduce from 'lodash/reduce';
import { sanitizeEntity } from './sanitize';
import { ALGOLIA_SEARCH_KEY_PARAMS_HIERARCHIAL, ALGOLIA_SEARCH_KEY_PARAMS_REFINEMENT, algoliaSortOptions, ALL_USER_TYPES, REVIEW_TYPE_OF_PROVIDER, SEARCH_KEY_PARAMS_HIERARCHIAL, SEARCH_KEY_PARAMS_REFINEMENT, STRIPE_ONBOARDING_RETURN_URL_FAILURE, STRIPE_ONBOARDING_RETURN_URL_RESTRICTED, STRIPE_ONBOARDING_RETURN_URL_SUCCESS, USER_TYPE_RESTRICTED, USER_TYPE_UNVERIFIED, USER_TYPE_VERIFIED, UserPermissions } from './types';
import { getUrlVariants } from './cloudinary';
import { createSlug } from './urlHelpers';
// NOTE: This file imports sanitize.js, which may lead to circular dependency

/**
 * Combine the given relationships objects
 *
 * See: http://jsonapi.org/format/#document-resource-object-relationships
 */
export const combinedRelationships = (oldRels, newRels) => {
  if (!oldRels && !newRels) {
    // Special case to avoid adding an empty relationships object when
    // none of the resource objects had any relationships.
    return null;
  }
  return { ...oldRels, ...newRels };
};

/**
 * Combine the given resource objects
 *
 * See: http://jsonapi.org/format/#document-resource-objects
 */
export const combinedResourceObjects = (oldRes, newRes) => {
  const { id, type } = oldRes;
  if (newRes.id.uuid !== id.uuid || newRes.type !== type) {
    throw new Error('Cannot merge resource objects with different ids or types');
  }
  const attributes = newRes.attributes || oldRes.attributes;
  const attributesOld = oldRes.attributes || {};
  const attributesNew = newRes.attributes || {};
  // Allow (potentially) sparse attributes to update only relevant fields
  const attrs = attributes ? { attributes: { ...attributesOld, ...attributesNew } } : null;
  const relationships = combinedRelationships(oldRes.relationships, newRes.relationships);
  const rels = relationships ? { relationships } : null;
  return { id, type, ...attrs, ...rels };
};

/**
 * Combine the resource objects form the given api response to the
 * existing entities.
 */
export const updatedEntities = (oldEntities, apiResponse, sanitizeConfig = {}) => {
  const { data, included = [] } = apiResponse;
  const objects = (Array.isArray(data) ? data : [data]).concat(included);

  const newEntities = objects.reduce((entities, curr) => {
    const { id, type } = curr;

    // Some entities (e.g. listing and user) might include extended data,
    // you should check if src/util/sanitize.js needs to be updated.
    const current = sanitizeEntity(curr, sanitizeConfig);

    entities[type] = entities[type] || {};
    const entity = entities[type][id.uuid];
    entities[type][id.uuid] = entity ? combinedResourceObjects({ ...entity }, current) : current;

    return entities;
  }, oldEntities);

  return newEntities;
};

/**
 * Denormalise the entities with the resources from the entities object
 *
 * This function calculates the dernormalised tree structure from the
 * normalised entities object with all the relationships joined in.
 *
 * @param {Object} entities entities object in the SDK Redux store
 * @param {Array<{ id, type }} resources array of objects
 * with id and type
 * @param {Boolean} throwIfNotFound wheather to skip a resource that
 * is not found (false), or to throw an Error (true)
 *
 * @return {Array} the given resource objects denormalised that were
 * found in the entities
 */
export const denormalisedEntities = (entities, resources, throwIfNotFound = true) => {
  const denormalised = resources.map(res => {
    const { id, type } = res;
    const entityFound = entities[type] && id && entities[type][id.uuid];
    if (!entityFound) {
      if (throwIfNotFound) {
        throw new Error(`Entity with type "${type}" and id "${id ? id.uuid : id}" not found`);
      }
      return null;
    }
    const entity = entities[type][id.uuid];
    const { relationships, ...entityData } = entity;

    if (relationships) {
      // Recursively join in all the relationship entities
      return reduce(
        relationships,
        (ent, relRef, relName) => {
          // A relationship reference can be either a single object or
          // an array of objects. We want to keep that form in the final
          // result.
          const hasMultipleRefs = Array.isArray(relRef.data);
          const multipleRefsEmpty = hasMultipleRefs && relRef.data.length === 0;
          if (!relRef.data || multipleRefsEmpty) {
            ent[relName] = hasMultipleRefs ? [] : null;
          } else {
            const refs = hasMultipleRefs ? relRef.data : [relRef.data];

            // If a relationship is not found, an Error should be thrown
            const rels = denormalisedEntities(entities, refs, true);

            ent[relName] = hasMultipleRefs ? rels : rels[0];
          }
          return ent;
        },
        entityData
      );
    }
    return entityData;
  });
  return denormalised.filter(e => !!e);
};

/**
 * Denormalise the data from the given SDK response
 *
 * @param {Object} sdkResponse response object from an SDK call
 *
 * @return {Array} entities in the response with relationships
 * denormalised from the included data
 */
export const denormalisedResponseEntities = sdkResponse => {
  const apiResponse = sdkResponse.data;
  const data = apiResponse.data;
  const resources = Array.isArray(data) ? data : [data];

  if (!data || resources.length === 0) {
    return [];
  }

  const entities = updatedEntities({}, apiResponse);
  return denormalisedEntities(entities, resources);
};

/**
 * Denormalize JSON object.
 * NOTE: Currently, this only handles denormalization of image references
 *
 * @param {JSON} data from Asset API (e.g. page asset)
 * @param {JSON} included array of asset references (currently only images supported)
 * @returns deep copy of data with images denormalized into it.
 */
const denormalizeJsonData = (data, included) => {
  let copy;

  // Handle strings, numbers, booleans, null
  if (data === null || typeof data !== 'object') {
    return data;
  }

  // At this point the data has typeof 'object' (aka Array or Object)
  // Array is the more specific case (of Object)
  if (data instanceof Array) {
    copy = data.map(datum => denormalizeJsonData(datum, included));
    return copy;
  }

  // Generic Objects
  if (data instanceof Object) {
    copy = {};
    Object.entries(data).forEach(([key, value]) => {
      // Handle denormalization of image reference
      const hasImageRefAsValue =
        typeof value == 'object' && value?._ref?.type === 'imageAsset' && value?._ref?.id;
      // If there is no image included,
      // the _ref might contain parameters for image resolver (Asset Delivery API resolves image URLs on the fly)
      const hasUnresolvedImageRef = typeof value == 'object' && value?._ref?.resolver === 'image';

      if (hasImageRefAsValue) {
        const foundRef = included.find(inc => inc.id === value._ref?.id);
        copy[key] = foundRef;
      } else if (hasUnresolvedImageRef) {
        // Don't add faulty image ref
        // Note: At the time of writing, assets can expose resolver configs,
        //       which we don't want to deal with.
      } else {
        copy[key] = denormalizeJsonData(value, included);
      }
    });
    return copy;
  }

  throw new Error("Unable to traverse data! It's not JSON.");
};

/**
 * Denormalize asset json from Asset API.
 * @param {JSON} assetJson in format: { data, included }
 * @returns deep copy of asset data with images denormalized into it.
 */
export const denormalizeAssetData = assetJson => {
  const { data, included } = assetJson || {};
  return denormalizeJsonData(data, included);
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} transaction entity object, which is to be ensured against null values
 */
export const ensureTransaction = (transaction, booking = null, listing = null, provider = null) => {
  const empty = {
    id: null,
    type: 'transaction',
    attributes: {},
    booking,
    listing,
    provider,
  };
  return { ...empty, ...transaction };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} booking entity object, which is to be ensured against null values
 */
export const ensureBooking = booking => {
  const empty = { id: null, type: 'booking', attributes: {} };
  return { ...empty, ...booking };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureListing = listing => {
  const empty = {
    id: null,
    type: 'listing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureOwnListing = listing => {
  const empty = {
    id: null,
    type: 'ownListing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} user entity object, which is to be ensured against null values
 */
export const ensureUser = user => {
  const empty = { id: null, type: 'user', attributes: { profile: {} } };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} current user entity object, which is to be ensured against null values
 */
export const ensureCurrentUser = user => {
  const empty = { id: null, type: 'currentUser', attributes: { profile: {} }, profileImage: {} };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} time slot entity object, which is to be ensured against null values
 */
export const ensureTimeSlot = timeSlot => {
  const empty = { id: null, type: 'timeSlot', attributes: {} };
  return { ...empty, ...timeSlot };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureDayAvailabilityPlan = availabilityPlan => {
  const empty = { type: 'availability-plan/day', entries: [] };
  return { ...empty, ...availabilityPlan };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureAvailabilityException = availabilityException => {
  const empty = { id: null, type: 'availabilityException', attributes: {} };
  return { ...empty, ...availabilityException };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensureStripeCustomer = stripeCustomer => {
  const empty = { id: null, type: 'stripeCustomer', attributes: {} };
  return { ...empty, ...stripeCustomer };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensurePaymentMethodCard = stripePaymentMethod => {
  const empty = {
    id: null,
    type: 'stripePaymentMethod',
    attributes: { type: 'stripe-payment-method/card', card: {} },
  };
  const cardPaymentMethod = { ...empty, ...stripePaymentMethod };

  if (cardPaymentMethod.attributes.type !== 'stripe-payment-method/card') {
    throw new Error(`'ensurePaymentMethodCard' got payment method with wrong type.
      'stripe-payment-method/card' was expected, received ${cardPaymentMethod.attributes.type}`);
  }

  return cardPaymentMethod;
};

/**
 * Get the display name of the given user as string. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned or deleted users, a translated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayNameAsString = (user, defaultUserDisplayName) => {
  const hasDisplayName = user?.attributes?.profile?.displayName;
  const userFirstName = user?.attributes?.profile?.firstName;
  if (hasDisplayName || userFirstName) {
    return hasDisplayName || userFirstName;
  } else {
    return defaultUserDisplayName || '';
  }
};

/**
 * DEPRECATED: Use userDisplayNameAsString function or UserDisplayName component instead
 *
 * @param {propTypes.user} user
 * @param {String} bannedUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayName = (user, bannedUserDisplayName) => {
  console.warn(
    `Function userDisplayName is deprecated!
User function userDisplayNameAsString or component UserDisplayName instead.`
  );

  return userDisplayNameAsString(user, bannedUserDisplayName);
};

/**
 * Get the abbreviated name of the given user. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned  or deleted users, a default abbreviated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserAbbreviatedName
 *
 * @return {String} abbreviated name that can be rendered in the UI
 * (e.g. in Avatar initials)
 */
export const userAbbreviatedName = (user, defaultUserAbbreviatedName) => {
  const hasAttributes = user && user.attributes;
  const hasProfile = hasAttributes && user.attributes.profile;
  const hasDisplayName = hasProfile && user.attributes.profile.abbreviatedName;

  if (hasDisplayName) {
    return user.attributes.profile.abbreviatedName;
  } else {
    return defaultUserAbbreviatedName || '';
  }
};

/**
 * A customizer function to be used with the
 * mergeWith function from lodash.
 *
 * Works like merge in every way exept that on case of
 * an array the old value is completely overridden with
 * the new value.
 *
 * @param {Object} objValue Value of current field, denoted by key
 * @param {Object} srcValue New value
 * @param {String} key Key of the field currently being merged
 * @param {Object} object Target object that is receiving values from source
 * @param {Object} source Source object that is merged into object param
 * @param {Object} stack Tracks merged values
 *
 * @return {Object} New value for objValue if the original is an array,
 * otherwise undefined is returned, which results in mergeWith using the
 * standard merging function
 */
export const overrideArrays = (objValue, srcValue, key, object, source, stack) => {
  if (isArray(objValue)) {
    return srcValue;
  }
};

/**
 * Humanizes a line item code. Strips the "line-item/" namespace
 * definition from the beginnign, replaces dashes with spaces and
 * capitalizes the first character.
 *
 * @param {string} code a line item code
 *
 * @return {string} returns the line item code humanized
 */
export const humanizeLineItemCode = code => {
  if (!/^line-item\/.+/.test(code)) {
    throw new Error(`Invalid line item code: ${code}`);
  }
  const lowercase = code.replace(/^line-item\//, '').replace(/-/g, ' ');

  return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
};

// necessary params get from response
export const onGetCloudnaryData = (data) => {
  // "asset_id": "d74e145b5a5da5aa55f4f9738bc23c71",
  //   "public_id": "672c8601-3714-41f9-b75f-0c22f2935725",
  //   "version": 1730971138,
  //   "version_id": "16403bd850f7ec46f211d914a08f9a3a",
  //   "signature": "607fae955be12446c4549c265113aa7f046e91c3",
  //   "width": 341,
  //   "height": 425,
  //   "format": "png",
  //   "resource_type": "image",
  //   "created_at": "2024-11-07T09:18:58Z",
  //   "tags": [],
  //   "bytes": 177125,
  //   "type": "upload",
  //   "etag": "34da4513f8323c93a7509ebd8389ed22",
  //   "placeholder": false,
  //   "url": "http://res.cloudinary.com/turnouthq-dev/image/upload/v1730971138/672c8601-3714-41f9-b75f-0c22f2935725.png",
  //   "secure_url": "https://res.cloudinary.com/turnouthq-dev/image/upload/v1730971138/672c8601-3714-41f9-b75f-0c22f2935725.png",
  //   "asset_folder": "dev",
  //   "display_name": "edit",
  //   "access_control": [
  //       {
  //           "access_type": "anonymous"
  //       }
  //   ],
  //   "existing": false,
  //   "original_filename": "edit"
  const { width, height, format, resource_type, created_at, bytes, type, secure_url, asset_folder, display_name, original_filename, public_id } = data;
  return { width, height, format, resource_type, created_at, bytes, type, secure_url, asset_folder, display_name, original_filename, public_id }
}

// check user verified or not
export const isVerifiedBadge = (user) => user?.attributes?.profile?.publicData?.verifiedType == USER_TYPE_VERIFIED;

// Helper function to get the value from an array by key
export const getValuesFromArray = (data, key) => {
  if (!Array.isArray(data)) return ""; // Return empty string if data isn't an array
  const item = data.find((st) => st?.id === key);
  return item ? item.name : ""; // Return name if found, otherwise empty string
};

// Helper function to get the label for a given option key
export const getLabelOptionValue = (data, key) => {
  if (!Array.isArray(data)) return ""; // Return empty string if data isn't an array
  const item = data.find((st) => st?.option === key);
  return item ? item.label : ""; // Return label if found, otherwise empty string
}

// get review of author
export const onGetAuthorReviews = (reviews) => {
  const reviewsOfProvider = Array.isArray(reviews) && reviews.length ? reviews.filter(r => r?.attributes?.type === REVIEW_TYPE_OF_PROVIDER) : [];
  const reviewRating = Array.isArray(reviewsOfProvider) && reviewsOfProvider.length
    ? (reviewsOfProvider.reduce((acc, curr) => acc + (curr.attributes.rating || 0), 0)) / reviewsOfProvider.length
    : null;
  return { reviewsOfProvider, reviewRating: typeof reviewRating == "number" ? Math.round(reviewRating) : null }
};

// Get the category value based on the given category levels
export const getCategoryValue = (categories, categoryLevel1, categoryLevel2) => {
  let categoryValue = "";

  if (Array.isArray(categories) && categories.length) {
    // First, try to get categoryLevel1 value
    if (categoryLevel1) {
      categoryValue = getValuesFromArray(categories, categoryLevel1);
    }

    // If categoryLevel2 is present, check for subcategories under categoryLevel2
    if (categoryLevel2) {
      const subCategory = categories.find((st) => st.id === categoryLevel2)?.subcategories;
      if (subCategory && Array.isArray(subCategory)) {
        categoryValue = getValuesFromArray(subCategory, categoryLevel1);
      }
    }
  }

  return categoryValue; // Return 'N/A' if no value is found
};

// Get value for public data based on field configurations
export const getPublicDataValue = (listingFieldConfigs, key, value) => {
  let publicValue = "";

  // Loop through each field configuration
  if (Array.isArray(listingFieldConfigs)) {
    listingFieldConfigs.forEach((config) => {
      const { enumOptions } = config;
      // If the key matches and enumOptions are provided, find the corresponding label
      if (key === config.key && Array.isArray(enumOptions)) {
        publicValue = getLabelOptionValue(enumOptions, value);
      }
    });
  }

  return publicValue; // Default to 'N/A' if no matching value is found
};


export const getUniqueAuthors = (listings) => {
  return [...new Set(listings.filter(st => st?.author?.id?.uuid).map((st) => st.author.id.uuid))];
};

export const getExactPrice = (num) => {
  return (Math.round((+num) * 100) / 100);
};

export const getTotalPricesOfListings = (listings, deliveryMethod) => {
  const subTotal = getExactPrice(listings.reduce((acc, curr) => acc + ((curr?.attributes?.price?.amount * curr.count) || 0), 0));
  const delivery = 0;
  const total = getExactPrice(subTotal + delivery);
  const currency = listings && listings.length ? listings[0].attributes?.price?.currency : "";
  return {
    total,
    subTotal,
    delivery,
    currency
  }
};

export const isUserSelectedGroup = (user) => Array.isArray(user?.attributes?.profile?.publicData?.selectedGroups) && user?.attributes?.profile?.publicData?.selectedGroups.length ? user?.attributes?.profile?.publicData?.selectedGroups : false;


// Function to calculate the age based on selected year
export const calculateAge = (yearOfBirth) => new Date().getFullYear() - (+yearOfBirth);

// Starting year for the dropdown
export const getAllYears = (startYear = 1900) => {
  const currentYear = new Date().getFullYear() - 13; // Get the current year
  return Array.from({ length: currentYear - startYear + 1 }, (_, index) => currentYear - index); // Create an array of years
}


// get user permission based on user Role
export const getUserPermissions = (userType, type) => {
  if (type && userType && ALL_USER_TYPES.includes(userType)) {
    return UserPermissions[type] && UserPermissions[type][userType];
  }
  else {
    return null;
  }
};

// check member Ship of User
export const getUserMembershipStatus = (currentUser) => {
  return currentUser?.attributes?.profile?.publicData?.userRole;
}

// get user Role
export const currentUserRole = (currentUser) => currentUser?.attributes?.profile?.publicData?.userRole;

export const getStripeVerificationStatus = (returnURLType, attempts = 0) => {
  switch (returnURLType) {
    case STRIPE_ONBOARDING_RETURN_URL_SUCCESS:
      return USER_TYPE_VERIFIED;

    case STRIPE_ONBOARDING_RETURN_URL_FAILURE:
      return attempts >= 4 ? USER_TYPE_RESTRICTED : USER_TYPE_UNVERIFIED;

    default:
      return USER_TYPE_UNVERIFIED;
  }
};

export const truncateTextByCharacters = (text, limit) => {
  if (typeof text !== 'string' || !text.trim()) {
    return ''; // Return an empty string if the input is invalid
  }

  return text.length > limit ? text.slice(0, limit) + '...' : text;
};


export const filterNullObjectValues = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => value !== null)
  );
};


// <<<<<<<<< restrictedValidRoutes work >>>>>>>>>>

export const checkRestrictMarketPlaceEnvKeys = () => {
  const USERNAME = process.env.REACT_APP_AUTH_USERNAME;
  const PASSWORD = process.env.REACT_APP_AUTH_PASSWORD;
  const hasUsername = typeof USERNAME === 'string' && USERNAME.length > 0;
  const hasPassword = typeof PASSWORD === 'string' && PASSWORD.length > 0;
  return hasUsername && hasPassword;
};

export const onCheckRestrictMarketplcaeCred = () => {
  if (checkRestrictMarketPlaceEnvKeys()) {
    const { username = "", password = "" } = (typeof window != "undefined" && window.localStorage && window.localStorage.getItem("authCred") && typeof JSON.parse(window.localStorage.getItem("authCred")) == "object") ? JSON.parse(window.localStorage.getItem("authCred")) : {};
    return username == process.env.REACT_APP_AUTH_USERNAME && password == process.env.REACT_APP_AUTH_PASSWORD;
  }
  else {
    return true;
  }
};

export const isListingIsAFirm = (listing) => listing?.attributes?.publicData?.offers_discount == "Price is firm";

export const getMinimumPriceFirmForOffer = (listing) => {
  const { publicData, price } = listing?.attributes || {};
  const { offers_discount } = publicData || {};
  const maxPrice = price?.amount;
  return (offers_discount == "Yes: for 75% +" && maxPrice) ? maxPrice * (0.75)
    : (offers_discount == "Yes: for 50% +" && maxPrice) ? maxPrice * (0.50)
      : 100;
};

/**
 * Extracts and formats user details from the user object.
 * @param {Object} user - The user object containing user attributes and profile information.
 * @returns {Object} - A formatted object with the user's details.
 */
export const onGetUserDetails = (user) => {
  if (!user || !user.attributes || !user.attributes.profile) {
    // Return a fallback object in case the user or expected attributes are missing
    return {
      userId: null,
      name: 'Unknown User',
      email: '',
      firstName: '',
      lastName: '',
      displayName: '',
    };
  }

  const { email, profile } = user.attributes;
  const { firstName, lastName, displayName, ...rest } = profile;

  // Ensure firstName and lastName exist before concatenating
  const name = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || displayName || 'Anonymous';

  return {
    userId: user.id?.uuid || null,  // Ensure uuid is accessed safely
    name,
    email,
    firstName: firstName || '',  // Default to empty string if firstName is missing
    lastName: lastName || '',    // Default to empty string if lastName is missing
    displayName: displayName || '', // Default to empty string if displayName is missing
    ...rest, // Include any additional profile attributes
  };
};

// <<<<<<<<<<<<<<<< create and update the listing data in algolia >>>>>>>>>>
/**
 * Creates or updates the data to be sent to Algolia.
 * @param {string} indexName - The Algolia index name.
 * @param {Object} listing - The listing object containing all the details.
 * @param {Object} currentUser - The current user object.
 * @param {Object} updateParams - Optional parameters for updating the existing data.
 * @returns {Object} - The formatted data to be sent to Algolia.
 */
export const makeCreateUpdateAlgoliaData = (indexName, listing, currentUser, updateParams) => {
  const listingId = listing.id.uuid;

  // If updateParams are provided, return the update data directly
  if (updateParams) {
    return {
      indexName,
      data: {
        ...updateParams,
        objectID: listingId, // Ensure the objectID is always attached
      }
    };
  }

  // Destructure user details, adding defaults to avoid errors if any field is missing
  const {
    name = '',
    email = '',
    firstName = '',
    lastName = '',
    displayName = '',
    userId,
    publicData: uPublicData = {},
  } = onGetUserDetails(currentUser);

  let seller_photo = uPublicData?.cloudProfileImage?.secure_url || "";

  // Destructure listing attributes with fallback values
  const {
    title = '',
    description = '',
    price,
    geolocation,
    state = '',
    publicData = {},
    createdAt
  } = listing.attributes || {};

  const { cloudListingImages, heroImageId } = publicData;

  let image_url = "";
  if (typeof cloudListingImages == "object" && Object.keys(cloudListingImages).length) {
    image_url = heroImageId && cloudListingImages[heroImageId] ? cloudListingImages[heroImageId] : Object.values(cloudListingImages).map(st => st)[0];
  };

  // Extract stock from included array if available
  let quantity = 0;
  if (Array.isArray(listing.included)) {
    listing.included.forEach((item) => {
      if (item.type === 'stock' && item.attributes?.quantity) {
        quantity = item.attributes.quantity;
      }
    });
  }

  // Safely handle price with defaults
  const formattedPrice = price?.amount && price?.currency ? price.amount / 100 : 0;
  const currency = price?.currency || null;

  // Geolocation handling (defaults to empty object if missing or invalid)
  const geoLoc = geolocation?.lat && geolocation?.lng ? { lat: geolocation.lat, lng: geolocation.lng } : {};

  // Format the createdAt timestamp if available, default to null if missing
  const formattedCreatedAt = createdAt ? moment(createdAt).unix() : null;

  // hierarchicalCategories
  let hierarchicalCategories = {};
  const hierarchicalCatName = ["categoryName", "subCategoryName", "subSubCategoryName"];
  const allCategories = hierarchicalCatName.map((st, i) => publicData[st]).filter(st => st);
  allCategories.forEach((it, i) => hierarchicalCategories[`lvl${i}`] = allCategories.filter((st, index) => index <= i).join(" > "));

  // Build the data to be indexed in Algolia
  const data = {
    objectID: listingId, // Unique identifier for Algolia
    _geoloc: geoLoc,  // Safe geolocation
    title,
    id: listingId,
    description,
    state,
    price: formattedPrice,  // Price with proper formatting
    currency,
    image_url,
    publicData, // Any additional public data
    date_created: formattedCreatedAt,  // Safe conversion of createdAt
    date_updated: moment().unix(),  // Current timestamp for updatedAt
    quantity,  // Stock, if available or set to default
    allCategories,
    hierarchicalCategories,
    // seller information
    seller_id: userId,
    seller_name: displayName,
    seller_photo,
    seller_rating: 5,
    seller_first_name: firstName,
    seller_last_name: lastName,
    seller_email: email,
    isDeleted: false
  };

  return {
    indexName,
    data
  };
};




// {
//   "RITA_DEV": {
//       "configure": {
//           "filters": "state:\"published\" AND (publicData.totalStockAvailable > 0)",
//           "ruleContexts": [],
//           "hitsPerPage": 12
//       },
//       "query": "bus",
//       "menu": {
//           "publicData.primaryCategory": "Pēpi",
//           "publicData.subCategory": "Wear"
//       },
//       "sortBy": "RITA_DEV_price_asc",
//       "range": {
//           "price": "1:15"
//       }
//   }
// }




export const makeAlgoliaSearchParams = (indexName, value) => {
  const searchParams = {};
  const { menu, query, sortBy, range, page, refinementList, hierarchicalMenu } = typeof value == "object" ? value[indexName] : {};

  // search query
  query && Object.assign(searchParams, { query: query });

  // page query
  page && Object.assign(searchParams, { page: page });

  // sortBy
  sortBy && algoliaSortOptions.filter((st) => st.value == sortBy).length
    && Object.assign(searchParams, { sort: algoliaSortOptions.filter((st) => st.value == sortBy)[0].searchKeyName });

  // price price "10:11"
  typeof range == "object" && range.price && range.price.includes(":")
    && Object.assign(searchParams, { price: range.price.split(":")[0] + "-" + range.price.split(":")[1] });

  // refinementList
  typeof refinementList == "object" && Object.keys(refinementList).length && Object.keys(refinementList).map((st) => SEARCH_KEY_PARAMS_REFINEMENT[st] && Array.isArray(refinementList[st]) && Object.assign(searchParams, {
    [SEARCH_KEY_PARAMS_REFINEMENT[st]]: refinementList[st].join(",")
  }));

  // SEARCH_KEY_PARAMS_HIERARCHIAL
  typeof hierarchicalMenu == "object" && Object.keys(hierarchicalMenu).length && Object.keys(hierarchicalMenu).map((st) => SEARCH_KEY_PARAMS_HIERARCHIAL[st] && Array.isArray(hierarchicalMenu[st]) && Object.assign(searchParams, {
    [SEARCH_KEY_PARAMS_HIERARCHIAL[st]]: hierarchicalMenu[st].join(",")
  }));

  return searchParams;
};

export const setAlgoliaInitailState = (indexName, value, configureProps = false, isSearchPage) => {
  const initialState = {
    [indexName]: {
      "configure": configureProps ? configureProps : {
        // "filters": "state:\"published\" AND (quantity > 0)",
        "ruleContexts": [],
        "hitsPerPage": 12
      },
      refinementList: {

      },
      menu: {

      },
      toggle: {

      },
      hierarchicalMenu: {

      }
    }
  };

  const {
    sort,
    price,
    query,
    page,
    ...rest
  } = typeof value == "object" ? value : {};

  // search query
  query && Object.assign(initialState[indexName], { query });

  // search query
  page && Object.assign(initialState[indexName], { page });

  // sortBy
  sort && algoliaSortOptions.filter((st) => st.searchKeyName == sort).length
    && Object.assign(initialState[indexName], { sortBy: algoliaSortOptions.filter((st) => st.searchKeyName == sort)[0].value });

  // price
  const newPrice = price ? price + "" : "";
  newPrice && Object.assign(initialState[indexName], { range: { price: newPrice.includes("-") ? newPrice.split("-")[0] + ":" + newPrice.split("-")[1] : newPrice } });

  // alll refinemnetList
  isSearchPage && typeof rest == "object" && Object.keys(rest).length
    && Object.keys(rest).map((st) => ALGOLIA_SEARCH_KEY_PARAMS_REFINEMENT[st] && Object.assign(initialState[indexName].refinementList, { [ALGOLIA_SEARCH_KEY_PARAMS_REFINEMENT[st]]: String(rest[st]).split(",") }));

  // alll refinemnetList
  isSearchPage && typeof rest == "object" && Object.keys(rest).length
    && Object.keys(rest).map((st) => ALGOLIA_SEARCH_KEY_PARAMS_HIERARCHIAL[st] && Object.assign(initialState[indexName].hierarchicalMenu, { [ALGOLIA_SEARCH_KEY_PARAMS_HIERARCHIAL[st]]: String(rest[st]).split(",") }));

  return initialState;
}

export const getShippingDetails = (currentUser, pageData, values = {}) => {
  const { shippingDimensions, shippingHeight, shippingLength, shippingWeight, shippingWidth } = pageData?.listing?.attributes?.publicData || {};
  const dimensions = shippingDimensions.match(/(\d+)"\s*x\s*(\d+)"/) || shippingDimensions.match(/(\d+)'\s*x\s*(\d+)'\s*x\s*(\d+)'/) ||
  [];

  const buyerShippingAddress = values.deliveryAddress ? getShippoAddresFromValues(currentUser, values.deliveryAddress) : getShippoAddress(currentUser, "deliveryAddress")

  const cartItems = pageData?.cartListings || [];

  const cartItemsWeight = cartItems?.reduce((sum, item) => {
    const { shippingWeight } = item.attributes.publicData || {};
    return sum + (shippingWeight ? parseFloat(shippingWeight) : 0);
  }, 0);

  const totalWeight = cartItems && cartItems.length > 0 ? cartItemsWeight : shippingWeight;

  // const cartListingDimensions = cartItems.map((item) => {
  //   const { shippingDimensions, shippingLength, shippingWidth, shippingHeight, shippingWeight } = item.attributes.publicData || {};

  //   // Extract dimensions using regex
  //   const dimensions = shippingDimensions ? shippingDimensions.match(/(\d+)"\s*x\s*(\d+)"/) || [] : [];

  //   return {
  //     distance_unit: "in",
  //     mass_unit: "lb",
  //     length: dimensions[1] ? dimensions[1] : shippingLength || 1,
  //     width: dimensions[2] ? dimensions[2] : shippingWidth || 1,
  //     height: dimensions[3] ? dimensions[3] : shippingHeight || 1,
  //     weight: shippingWeight ? shippingWeight : 0,
  //   };
  // });

  const shippoShippingDetails = {
    authorId: pageData?.listing?.author?.id?.uuid,
    buyerShippingAddress,
    listingDimensions: (shippingDimensions && dimensions.length > 0) ?
      { length: dimensions[1], width: dimensions[2], height: dimensions[3] || 1, weight: totalWeight ? totalWeight : 0 }
      : { length: shippingLength, width: shippingWidth, height: shippingHeight || 1, weight: totalWeight }
  };

  return shippoShippingDetails;
};

export const getShippoAddresFromValues = (user, Address) => {
  const { profile, email } = user?.attributes || {};
  const { displayName, protectedData, firstName, lastName } = profile || {};
  const { phoneNumber } = protectedData || {};
  const { selectedPlace } = Address || {};
  const { address, city, selectedState, zip, countryCode } = selectedPlace || {};
  return {
    name: `${firstName} ${lastName}`,
    street1: address,
    city,
    state: selectedState,
    zip,
    country: countryCode,
    phone: phoneNumber,
    email,
  };
}

// key==shippingAddress
export const getShippoAddress = (user, key) => {
  const { profile, email  } = user?.attributes || {};
  const { displayName, publicData, protectedData, firstName, lastName } = profile || {};
  const { phoneNumber } = protectedData || {};
  const { selectedPlace } = publicData[key] || {};
  const { address, city, selectedState, zip, countryCode } = selectedPlace || {};
  return {
    name: `${firstName} ${lastName}`,
    street1: address,
    city,
    state: selectedState,
    zip,
    country: countryCode || publicData.countryCode || "US", // Adjust if needed
    phone: phoneNumber,
    email,
  };
}

export const isValidShippoAdress = (user, key) => {
  const { name, street1, city, state, zip, country } = getShippoAddress(user, key);
  return name && street1 && city && state && zip && country;
};

export const cartItemsCount = (cartData) => {
  let totalQuantity = 0;
  try {
    for (const cartId in cartData) {
      const items = cartData[cartId];
      for (const productId in items) {
        if (items[productId]?.count) {
          totalQuantity += 1;
          // totalQuantity += items[productId].count;
        }
      }
    };
    return totalQuantity;
  } catch (error) {
    return totalQuantity;
  }
};

export const convertImageURLwebp = (imageUrl) => {
  try {
    return imageUrl.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  } catch (e) {
    return imageUrl;
  }
};

export const convertCardImageURLwebp = (url) => {
  if (!url) return "";
  return url.replace("/upload/", "/upload/w_800,h_533,c_fit,f_auto,q_auto/").replace(/\.(jpg|jpeg|png)$/i, '.webp');
};

export const getSortedListingImages = (publicData) => {
  try {
    const { cloudListingImages: cloudnaryImages = {}, heroImageId } = publicData || {};
    // Convert object to array and sort based on heroImageId
    const sortedEntries = Object.entries(cloudnaryImages).sort(([keyA], [keyB]) => {
      if (keyA === heroImageId) return -1; // Place heroImageId first
      if (keyB === heroImageId) return 1;
      return keyA.localeCompare(keyB); // Sort remaining keys in ascending order
    });
    // Convert back to object
    const sortedImages = Object.fromEntries(sortedEntries);
    return Object.values(sortedImages).map(getUrlVariants).filter(st => st);
  } catch (e) {
    console.log(e, '&&& &&& => e');
    return [];
  }
};

export const getListingCatSubCatRouteParams = (listing) => {
  try {
    const id = listing?.id?.uuid || "1111";
    const { title, state, publicData } = listing?.attributes || {};
    const {
      subCategoryName,
      categoryName
    } = publicData || {};
    return {
      id,
      slug: title ? createSlug(title) : "title",
      category: categoryName ? createSlug(categoryName) : "category",
      subCategory: subCategoryName ? createSlug(subCategoryName) : "sub-category"
    }
  } catch (error) {
    console.log(error, '&&& &&& => error');
    return {
      id: "1111",
      slug: "none",
      category: "category",
      subCategory: "sub-category"
    }
  }
};

export const getPickUpAddressOfUser = (user) => {
  const { city = "" } = user?.attributes?.profile?.publicData?.pickupAddress?.selectedPlace || {};
  return {
    city
  };
};

export const capitalizeWords = (str = '') => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};
// Starting year from 13 for the dropdown
export const getYearsFrom13 = (startYear = 2007) => {
  const currentYear = new Date().getFullYear() - 13; // Get the current year
  return Array.from({ length: currentYear - startYear + 1 }, (_, index) => currentYear - index); // Create an array of years
};
